/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  IconButton,
  Avatar,
  Rating,
  Card,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import ImageIcon from "@mui/icons-material/Image";
import RateReviewIcon from "@mui/icons-material/RateReview";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import ArgonInput from "components/ArgonInput";

function StudentReviewDialog({
  open,
  onClose,
  plan,
  review,
  onAddReview,
  onSave,
  onDeleteReview,
  loading,
}) {
  const [newReview, setNewReview] = useState({
    studentName: "",
    rating: 5,
    review: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState("");
  const [errors, setErrors] = useState({});

  const isEditMode = !!review;

  useEffect(() => {
    if (review) {
      setNewReview({
        studentName: review.studentName || "",
        rating: review.rating || 5,
        review: review.review || "",
        photo: null,
      });
      setPhotoPreview(review.photo || "");
    } else {
      setNewReview({
        studentName: "",
        rating: 5,
        review: "",
        photo: null,
      });
      setPhotoPreview("");
    }
    setErrors({});
  }, [review, open]);

  const handleChange = (e) => {
    setNewReview({
      ...newReview,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setNewReview({
        ...newReview,
        photo: file,
      });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRatingChange = (event, newValue) => {
    setNewReview({
      ...newReview,
      rating: newValue || 1,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newReview.studentName.trim()) {
      newErrors.studentName = "Student name is required";
    }
    if (!newReview.review.trim()) {
      newErrors.review = "Review text is required";
    }
    if (newReview.rating < 1 || newReview.rating > 5) {
      newErrors.rating = "Rating must be between 1 and 5";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      if (isEditMode) {
        onSave(newReview);
      } else {
        onAddReview(newReview);
        setNewReview({
          studentName: "",
          rating: 5,
          review: "",
          photo: null,
        });
        setPhotoPreview("");
      }
      setErrors({});
    }
  };

  const handleDeleteReview = (reviewId) => {
    onDeleteReview(reviewId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <ArgonTypography variant="h5" fontWeight="bold">
              {isEditMode ? "Edit Student Review" : "Student Reviews"}
            </ArgonTypography>
            {!isEditMode && plan && (
              <ArgonTypography variant="caption" color="text">
                {plan.planName} Plan Reviews
              </ArgonTypography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Add/Edit Review Section */}
        <Card
          sx={{
            background: "linear-gradient(180deg, #ffffff 0%, #f6f9fc 100%)",
            border: "1px solid #e0e7ff",
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              {isEditMode ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
              <ArgonTypography variant="h6" fontWeight="bold">
                {isEditMode ? "Edit Review" : "Add New Review"}
              </ArgonTypography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                  Student Name <span style={{ color: "#ffeb3b" }}>*</span>
                </ArgonTypography>
                <ArgonInput
                  name="studentName"
                  value={newReview.studentName}
                  onChange={handleChange}
                  placeholder="Enter student name"
                  required
                />
                {errors.studentName && (
                  <ArgonTypography variant="caption" color="error">
                    {errors.studentName}
                  </ArgonTypography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                  Photo Upload
                </ArgonTypography>
                <Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <ArgonButton
                      component="span"
                      color="info"
                      variant="outlined"
                      size="small"
                      fullWidth
                    >
                      <ImageIcon sx={{ mr: 1 }} />
                      Choose Image
                    </ArgonButton>
                  </label>
                  {photoPreview && (
                    <Box mt={2} textAlign="center">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "150px",
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                  Rating <span style={{ color: "#ffeb3b" }}>*</span>
                </ArgonTypography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Rating
                    name="rating"
                    value={newReview.rating}
                    onChange={handleRatingChange}
                    size="large"
                    icon={<StarIcon fontSize="inherit" />}
                    emptyIcon={<StarIcon fontSize="inherit" />}
                  />
                  <Chip
                    label={`${newReview.rating} Star${newReview.rating > 1 ? "s" : ""}`}
                    color="warning"
                    size="small"
                  />
                </Box>
                {errors.rating && (
                  <ArgonTypography variant="caption" color="error">
                    {errors.rating}
                  </ArgonTypography>
                )}
              </Grid>

              <Grid item xs={12}>
                <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                  Review <span style={{ color: "#ffeb3b" }}>*</span>
                </ArgonTypography>
                <ArgonInput
                  name="review"
                  value={newReview.review}
                  onChange={handleChange}
                  placeholder="Write your review here..."
                  required
                  multiline
                  rows={3}
                />
                {errors.review && (
                  <ArgonTypography variant="caption" color="error">
                    {errors.review}
                  </ArgonTypography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Existing Reviews Section - only show if not edit mode */}
        {!isEditMode && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <ArgonTypography variant="h6" fontWeight="bold">
                Existing Reviews
              </ArgonTypography>
              <Chip
                label={`${plan?.studentReviews?.length || 0} Review${
                  plan?.studentReviews?.length !== 1 ? "s" : ""
                }`}
                color="primary"
                size="small"
              />
            </Box>

            {plan?.studentReviews?.length === 0 ? (
              <Box
                textAlign="center"
                py={5}
                sx={{
                  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  borderRadius: 2,
                }}
              >
                <RateReviewIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                <ArgonTypography variant="h6" color="text">
                  No reviews yet
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">
                  Be the first to add a review!
                </ArgonTypography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {plan?.studentReviews?.map((reviewItem, index) => (
                  <Card
                    key={reviewItem._id || index}
                    variant="outlined"
                    sx={{
                      transition: "all 0.3s",
                      "&:hover": {
                        boxShadow: 3,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box display="flex" gap={2} flex={1}>
                          <Avatar
                            src={reviewItem.photo}
                            alt={reviewItem.studentName}
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: "primary.main",
                              fontSize: "1.5rem",
                              fontWeight: "bold",
                            }}
                          >
                            {reviewItem.studentName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="h6" fontWeight="bold">
                              {reviewItem.studentName}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Rating value={reviewItem.rating} readOnly size="small" />
                              <Typography variant="caption" color="text.secondary">
                                ({reviewItem.rating}/5)
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              {reviewItem.review}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(reviewItem.createdAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteReview(reviewItem._id)}
                          disabled={loading}
                          sx={{
                            "&:hover": {
                              backgroundColor: "error.light",
                              color: "white",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <ArgonButton onClick={onClose} color="secondary" variant="outlined" disabled={loading}>
          Cancel
        </ArgonButton>
        {isEditMode && (
          <ArgonButton
            onClick={() => onSave(newReview)}
            color="info"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Review"}
          </ArgonButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default StudentReviewDialog;
